import { Card, CardContent, CardHeader, CardTitle, EmptyState, ErrorState, Skeleton } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { ChatMessage } from "@/components/ai-chat/chat-message";
import { SearchBar } from "@/components/ui/search-bar";
import { PERMISSIONS } from "@/constants/permissions";
import { useUser } from "@/hooks/queries/user.queries";
import { useGetDarwinTranscript } from "@/queries/darwin.queries";
import { formatDateRelative } from "@/utils/format-date";
import { APP_ROUTES_FULL } from "@/utils/routes";
import { searchValidator } from "@/utils/search-validator";

const ConversationSearchSchema = z.object({
    q: z.string().optional(),
});

export const Route = createFileRoute(APP_ROUTES_FULL.DEVELOPER_DARWIN_CONVERSATION)({
    component: DarwinConversationPage,
    validateSearch: searchValidator(ConversationSearchSchema),
    staticData: {
        config: {
            title: "developer_darwin_conversation_title",
            breadcrumb: { title: "developer_darwin_conversation_title" },
        },
        permissions: [PERMISSIONS.CALL_SVC_ADMIN_BASE],
    },
});

function ConversationSearch({
    query,
    placeholder,
    onSearch,
}: {
    query: string;
    placeholder: string;
    onSearch: (value: string) => void;
}) {
    const [value, setValue] = useState(query);

    return (
        <SearchBar
            className="sm:w-80"
            value={value}
            onChange={setValue}
            onDebouncedChange={onSearch}
            placeholder={placeholder}
        />
    );
}

function DarwinConversationPage() {
    const { t } = useTranslation();
    const navigate = Route.useNavigate();
    const { conversationId } = Route.useParams();
    const { q } = Route.useSearch();

    const { data, isLoading, isError } = useGetDarwinTranscript(conversationId);
    const { data: user } = useUser(data?.userId);

    const onSearch = useCallback(
        (value: string) => {
            const nextQ = value || undefined;
            navigate({
                search: (prev) => ((prev.q || undefined) === nextQ ? prev : { ...prev, q: nextQ }),
                replace: true,
            });
        },
        [navigate]
    );

    const query = (q ?? "").trim().toLowerCase();
    const messages = useMemo(() => {
        const all = data?.messages ?? [];
        if (!query) return all;
        return all.filter((message) => message.content.toLowerCase().includes(query));
    }, [data?.messages, query]);

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <span className="flex min-w-0 flex-col gap-1">
                        <span className="flex items-center gap-2">
                            {user?.name ?? data?.userId ?? t("developer_darwin_conversation_title")}
                            {data ? (
                                <span className="text-sm font-normal text-muted-foreground">
                                    {t("developer_darwin_messages_total", { count: messages.length })}
                                </span>
                            ) : null}
                        </span>
                        {data ? (
                            <span className="truncate text-xs font-normal text-muted-foreground">
                                {data.lastPagePath} · {formatDateRelative(data.updatedAt)}
                            </span>
                        ) : null}
                    </span>
                    <ConversationSearch
                        query={q ?? ""}
                        placeholder={t("developer_darwin_conversation_search")}
                        onSearch={onSearch}
                    />
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {isError ? <ErrorState title={t("developer_darwin_conversation_loading_error")} /> : null}
                {isLoading ? (
                    <div className="space-y-3">
                        <Skeleton className="h-16 w-2/3" />
                        <Skeleton className="ml-auto h-16 w-1/2" />
                        <Skeleton className="h-24 w-full" />
                    </div>
                ) : null}
                {!isError && !isLoading && messages.length === 0 ? (
                    <EmptyState
                        variant="simple"
                        title={
                            query
                                ? "developer_darwin_conversation_no_matches"
                                : "developer_darwin_conversation_no_messages"
                        }
                    />
                ) : null}
                {!isError && !isLoading
                    ? messages.map((message) => (
                          <ChatMessage
                              key={message.id}
                              message={{
                                  id: message.id,
                                  role: message.role,
                                  content: message.content,
                                  createdAt: message.createdAt,
                              }}
                          />
                      ))
                    : null}
            </CardContent>
        </Card>
    );
}
